import type { CellContext } from '@tanstack/react-table';
import type { DataGridColumnDef, DataGridColumnDefOptions } from '../../data-grid.type';
import { formatPrice } from '@utils';

/**
 * Options for financial cell formatting
 * @interface FinCellOptions
 * @property {string} [currency='usd'] - Currency code to use for formatting (default: 'usd')
 */
export interface FinCellOptions {
	currency?: string;
}

/**
 * Creates a cell renderer for financial numbers with proper formatting
 *
 * @param col - The column definition
 * @param options - Additional options including parse function and financial options
 * @returns A cell renderer function that creates formatted financial numbers
 *
 * @example
 * // Basic usage with USD (default)
 * {
 *   key: 'amount',
 *   header: 'Amount',
 *   type: 'fin'
 * }
 *
 * @example
 * // With specific currency option
 * {
 *   key: 'amount',
 *   header: 'Amount',
 *   type: 'fin',
 *   finOptions: { currency: 'eur' }
 * }
 */
export function getColumnDefFin<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
	return function FinCell(context: CellContext<T, unknown>) {
		const value = context.getValue();
		const row = context.row.original;

		if (value === null || value === undefined) {
			return '';
		}

		let numberValue: number;

		// Parse the value
		if (options?.parse) {
			const parsed = options.parse(value, row, options);
			numberValue = typeof parsed === 'number' ? parsed : Number(parsed);
		} else {
			numberValue = Number(value);
		}

		// Check if it's a valid number
		if (isNaN(numberValue)) {
			return '';
		}

		// Get currency options
		const { currency }: FinCellOptions = options.finOptions || {};

		// Format the number
		return formatPrice(numberValue, { currency, locale: options.locale });
	};
}
